import React, { useState } from 'react';
import createTrack from 'services/actions/tracker_actions';
import {
  STRATEGIC_QUESTION_GROUPS,
  StrategicQuestion,
  StrategicQuestionGroup,
  StrategicQuestionSubgroup,
} from './strategic_question_groups';

export type {
  StrategicQuestion,
  StrategicQuestionGroup,
  StrategicQuestionSubgroup,
} from './strategic_question_groups';
export { STRATEGIC_QUESTION_GROUPS } from './strategic_question_groups';

interface StrategicQuestionsPanelProps {
  onCloseDrawer: () => void;
  sendMessage: (
    content: string,
    options?: { strategicQuestionId?: string },
  ) => void;
}

const StrategicQuestionsPanel: React.FC<StrategicQuestionsPanelProps> = ({
  onCloseDrawer,
  sendMessage,
}) => {
  const groups = STRATEGIC_QUESTION_GROUPS;

  const [openById, setOpenById] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    groups.forEach((g) => {
      initial[g.id] = false;
    });
    return initial;
  });

  const toggleSection = (id: string) => {
    setOpenById((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleQuestionClick = (question: StrategicQuestion) => {
    createTrack('Pergunte ao Edital - usou pergunta estratégica', {
      strategic_question_id: question.id,
    });
    sendMessage(question.text, { strategicQuestionId: question.id });
    onCloseDrawer();
  };

  const renderQuestions = (
    list: StrategicQuestion[],
    groupId: string,
    scopeId: string,
    isOpen: boolean,
  ) => (
    <ul className="list-unstyled bidding-chat-strategic-question-list mb-0 pb-1">
      {list.map((q) => (
        <li key={`${groupId}:${scopeId}:${q.id}`}>
          <button
            type="button"
            tabIndex={isOpen ? 0 : -1}
            className="btn bidding-chat-strategic-question-btn text-left w-100 py-2"
            onClick={() => handleQuestionClick(q)}
          >
            {q.text}
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="bidding-chat-strategic-content">
      <div className="bidding-chat-strategic-scroll">
        <div className="bidding-chat-strategic-accordion px-3 py-3">
          {groups.map((group: StrategicQuestionGroup) => {
            const isOpen = Boolean(openById[group.id]);

            return (
              <section
                key={group.id}
                className="bidding-chat-strategic-accordion-item mb-2"
                aria-label={group.title}
              >
                <button
                  type="button"
                  id={`bidding-chat-strategic-head-${group.id}`}
                  className="bidding-chat-strategic-accordion-head btn btn-block text-left d-flex align-items-center justify-content-between"
                  aria-expanded={isOpen}
                  aria-controls={`bidding-chat-strategic-body-${group.id}`}
                  onClick={() => toggleSection(group.id)}
                >
                  <span className="bidding-chat-strategic-accordion-title">{group.title}</span>
                  <i className="material-icons bidding-chat-strategic-accordion-icon" aria-hidden>
                    {isOpen ? 'expand_less' : 'expand_more'}
                  </i>
                </button>

                <div
                  id={`bidding-chat-strategic-body-${group.id}`}
                  role="region"
                  className={`bidding-chat-strategic-accordion-collapse ${isOpen ? 'is-open' : ''}`}
                  aria-labelledby={`bidding-chat-strategic-head-${group.id}`}
                  aria-hidden={!isOpen}
                >
                  <div className="bidding-chat-strategic-accordion-collapse-inner">
                    <div className="pt-2">
                      {group.preambleQuestions && group.preambleQuestions.length > 0
                        ? renderQuestions(
                            group.preambleQuestions,
                            group.id,
                            'preamble',
                            isOpen,
                          )
                        : null}

                      {group.subgroups?.map((sg: StrategicQuestionSubgroup) => (
                        <React.Fragment key={sg.id}>
                          <h6 className="bidding-chat-strategic-subgroup-heading">{sg.title}</h6>
                          {renderQuestions(sg.questions, group.id, sg.id, isOpen)}
                        </React.Fragment>
                      ))}

                      {group.questions && group.questions.length > 0
                        ? renderQuestions(group.questions, group.id, 'main', isOpen)
                        : null}
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StrategicQuestionsPanel;
